import React, { useContext, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import { InputText } from 'primereact/inputtext';
import { Chips } from 'primereact/chips';
import { MultiSelect } from 'primereact/multiselect';
import { fedexAccountLabel } from '@components/RingilComponents/fedex/fedexAccountLabel.ts';
import { Button } from 'primereact/button';
import {
  ACLInDTO,
  CMRSigningDataInDTO,
  CompanyLocationContactPersonOutDTO,
  CompanyLocationRampWithOpeningHoursOutDTO,
  FeaturesEnum,
  FedexAccountOutDTO,
  FileV2InDTO,
  LocationSharingInDTO,
  LovDTO,
  LovWithoutIdDTO,
  ModulesEnum,
  TvDashboardSettingsDTO,
  VisibilityEnum,
} from '@api/ringil-api.v2';
import ValidationDiv, { findValidationMessage } from '@utils/validation';
import useForm from '@hooks/useForm/useForm.tsx';
import { Col, Row } from '@components/styles.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import InputCountryPlain from '@components/obsolete/Form/InputCountry/InputCountryPlain.tsx';
import InputYesNoPlain from '@components/obsolete/Form/InputYesNoPlain/InputYesNoPlain.tsx';
import InputACL from '@components/obsolete/Form/InputACL/InputACL.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import UploadPanelVisual from '@components/framework/uploadpanel/UploadPanelVisual.tsx';
import SvgDocument from '@app/pages/attachment/Document.tsx';
import SvgDeleteIcon from '@app/pages/attachment/DeleteIcon.tsx';
import SvgTrashBin from '@icons/delete-trash.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import COItemDisplay from '@app/pages/COItemDisplay/COItemDisplay.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import InputTimezone from '@components/ringil3/Input/InputTimezone/InputTimezone.tsx';
import RichTextEditor from '@components/RingilComponents/RichTextEditor/RichTextEditor.tsx';
import { NOTE_SOFT_LIMIT } from '@components/RingilComponents/RichTextEditor/constants.ts';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import styled from 'styled-components';
import { useApplyResolvedTimezone } from '@hooks/useResolveTimezone/useResolveTimezone';
import TvDashboardSettingsEditor, { EMPTY_TV_DASHBOARD_SETTINGS } from '@components/ringil3/Features/Location/TvDashboardSettingsEditor.tsx';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  onCompleted: () => void;
  locationId?: string;
  timezoneId?: string;
  country?: string;
}

interface FormData {
  name: string;
  city: string;
  postalCode: string;
  streetNr: string;
  country: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  contactAutomaticPrefillToNotify: boolean;
  concurrentlyActiveEventsCount: number;
  isNotIncludedPrivateRamps: boolean;
  contacts: CompanyLocationContactPersonOutDTO[];
  acl: ACLInDTO;
  isExternal: boolean;
  externalName?: string;
  tvDashboard: TvDashboardSettingsDTO;
  tvMaxColumns?: number;
  isPartnerLocation: boolean;
  dachserBranch?: string;
  dachserCustomerNumber?: string;
  dachserToken?: string;
  schenkerReferenceNumber?: string;
  dhlAccountNumberExport?: string;
  dhlAccountNumberImport?: string;
  // FedEx accounts allowed for the location; empty = all company accounts
  fedexAccountIds?: string[];
  ramps: CompanyLocationRampWithOpeningHoursOutDTO[],
  isDeleted: boolean;
  externalId?: string;
  externalIds: string[];
  enableVisitor: boolean;
  senderSigningEnabled: boolean;
  senderSigningData?: CMRSigningDataInDTO;
  receiverSigningEnabled: boolean;
  receiverSigningData?: CMRSigningDataInDTO;
  timezoneId: string;
  sharing: LocationSharingInDTO[];
  locationNote?: string;
}

const initialData: FormData = {
  country: '',
  streetNr: '',
  city: '',
  name: '',
  contactEmail: '',
  contactName: '',
  contactPhone: '',
  contactAutomaticPrefillToNotify: false,
  postalCode: '',
  concurrentlyActiveEventsCount: null,
  isNotIncludedPrivateRamps: false,
  contacts: [],
  acl: { visibility: VisibilityEnum.PUBLIC, visibleToCompanies: [] },
  isExternal: false,
  externalName: null,
  tvDashboard: EMPTY_TV_DASHBOARD_SETTINGS,
  isPartnerLocation: false,
  ramps: [],
  isDeleted: false,
  externalId: null,
  externalIds: [],
  enableVisitor: false,
  senderSigningEnabled: false,
  senderSigningData: null,
  receiverSigningEnabled: false,
  receiverSigningData: null,
  timezoneId: '',
  sharing: [],
  locationNote: '',
};

const AddEditLocationSidebar: React.FC<ComponentProps> = ({ visible, onHide, onCompleted, locationId, timezoneId, country }) => {
  const { tr } = useTranslationLgs();
  const {
    createCompanyLocations,
    getCompanyLocationById,
    updateCompanyLocations,
    lovAllCompaniesByInheritedModules,
    lovLocationSharingPrivileges,
    listFedexAccounts,
  } = useContext(ApiContext);
  const { loggedUser, hasFeature } = useContext(AuthenticatedUserContext);
  const inputFileReceiverRef = useRef<HTMLInputElement | null>(null);
  const inputFileSenderRef = useRef<HTMLInputElement | null>(null);

  const cmrSigningEnabled = hasFeature([FeaturesEnum.SIGNING_CMR]);
  const [companies, setCompanies] = useState<LovDTO[]>();
  const [sharingPrivilegies, setSharingPrivilegies] = useState<LovWithoutIdDTO[]>();
  const fedexEnabled = hasFeature([FeaturesEnum.FEDEX_API]);
  const [fedexAccounts, setFedexAccounts] = useState<FedexAccountOutDTO[]>([]);

  useEffect(() => {
    if (visible && fedexEnabled) listFedexAccounts(undefined, undefined, data => setFedexAccounts(data));
  }, [visible, fedexEnabled]);

  useEffect(() => {
    lovAllCompaniesByInheritedModules({ modules: [ModulesEnum.TIMESLOTS] }, data => {
      setCompanies(data.filter(company => company.active));
    });
    lovLocationSharingPrivileges(data => {
      setSharingPrivilegies(data);
    });
  }, []);


  useEffect(() => {
    if (locationId) {
      getCompanyLocationById(locationId, d => {
        form.setValues({
          ...d,
          senderSigningData: d.senderSigningData
            ? { updateImage: false, ...d.senderSigningData }
            : null,
          receiverSigningData: d.receiverSigningData
            ? { updateImage: false, ...d.receiverSigningData }
            : null,
          isNotIncludedPrivateRamps: d.isIncludedPrivateRamps,
        });
      });
    } else if (visible && !locationId) {
      form.setFieldValue(names.country, country || 'CZE');
      form.setFieldValue(names.timezoneId, timezoneId || 'UTC');
    }
  }, [locationId, visible, country, timezoneId]);

  const validationSchema = yup.object().shape({
    name: yup.string().required(tr(`validation.required`, 'Value is required')),
    postalCode: yup.string().required(tr(`validation.required`, 'Value is required')),
    city: yup.string().required(tr(`validation.required`, 'Value is required')),
    streetNr: yup.string().required(tr(`validation.required`, 'Value is required')),
    contactEmail: yup.string().nullable().email(tr(`validation.valid_email`, 'Value has to be valid email address.')),
    contacts: yup.array().of(
      yup.object().shape({
        contactEmail: yup.string().email(tr(`validation.valid_email`, 'Value has to be valid email address.')).required(tr(`validation.required`, 'Value is required')),
        contactName: yup.string().required(tr(`validation.required`, 'Value is required')),
      }),
    ),
    acl: yup.mixed().test({
      name: 'required',
      test: (value: any, testContext) => {
        const v = testContext.parent as FormData;
        if (v.acl.visibility === VisibilityEnum.VISIBLE_TO_COMPANIES && v.acl.visibleToCompanies.length === 0) return false;
        return true;
      },
      message: 'required',
      exclusive: true,
    }),
    sharing: yup.array().of(
      yup.object().shape({
        companyId: yup.string().required(tr(`validation.required`, 'Value is required')),
        locationSharingPrivilege: yup.string().required(tr(`validation.required`, 'Value is required')),
      }),
    ),
  });

  const form = useForm<FormData>(validationSchema, initialData, values => save(values), false, false);
  const names = form.names;

  useApplyResolvedTimezone(
    { countryCode3: form.values.country, city: form.values.city, postalCode: form.values.postalCode },
    tz => form.setFieldValue(names.timezoneId, tz),
  );

  const addContactPerson = () => {
    form.setFieldValue(names.contacts, [...form.values.contacts, {}]);
  };
  const removeContactPerson = (deleteIndex: number) => {
    form.setFieldValue(
      names.contacts,
      form.values.contacts.filter((_, index) => index != deleteIndex),
    );
  };

  const save = (values: FormData) => {
    if (locationId) {
      updateCompanyLocations(locationId, values, () => {
        onCompleted();
      });
    } else {
      createCompanyLocations(values, () => {
        onCompleted();
      });
    }
  };


  function processFiles(signingData: string, fileList: File[]) {
    if (fileList?.length > 0) {
      const file = fileList[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileDto: FileV2InDTO = {
          fileName: file.name,
          dataURL: e.target?.result as string,
        };
        form.setFieldValue(`${signingData}.imageData`, fileDto);
        form.setFieldValue(`${signingData}.updateImage`, true);
      };
      reader.readAsDataURL(file);
    }
  }

  const handleFileSelection = (formName: string, event: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(formName, Array.from(event.target.files ?? []));
  };

  const handleClick = (inputRef: React.RefObject<HTMLInputElement>) => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  function deleteFile(formName: string) {
    form.setFieldValue(formName, null);
  }

  return (
    <RingilSidebar
      visible={visible}
      heading={tr(`AddEditLocationSidebar.addEditLocality`, `Add/Edit locality`)}
      onBack={onHide}
      onContinue={() => form.validateAndSend()}
      continueLabel={tr(`AddEditLocationSidebar.save`, `Save`)}
      maxWidth={'40rem'}
    >
      <ColumnRow>
          <Row>
            <HorizontalFieldValue
              label={tr(`AddEditLocationSidebar.localityName`, `Locality name`)}
              value={
                <>
                  <InputText style={{ width: '100%' }} value={form.values.name}
                             onChange={e => form.setFieldValue(names.name, e.target.value)} />
                  <Error>{form.errors.name}</Error>
                </>
              }
            />
          </Row>
          <Row>
            <HorizontalFieldValue
              label={tr(`AddEditLocationSidebar.state`, `State`)}
              value={
                <>
                  <InputCountryPlain onChange={v => form.setFieldValue(names.country, v)}
                                     codeValue={form.values.country} style={{ width: '100%' }} />
                  <Error>{form.errors.country}</Error>
                </>
              }
            />
            <HorizontalFieldValue
              label={tr(`AddEditLocationSidebar.zipCode`, `Zip code`)}
              value={
                <>
                  <InputText
                    style={{ width: '100%' }}
                    name={'postalCode'}
                    value={form.values.postalCode}
                    onChange={e => form.setFieldValue(names.postalCode, e.target.value)}
                  />
                  <Error>{form.errors.postalCode}</Error>
                </>
              }
            />
          </Row>
          <Row>
            <HorizontalFieldValue
              label={tr(`AddEditLocationSidebar.city`, `City`)}
              value={
                <>
                  <InputText style={{ width: '100%' }} name={'city'} value={form.values.city}
                             onChange={e => form.setFieldValue(names.city, e.target.value)} />
                  <Error>{form.errors.city}</Error>
                </>
              }
            />
            <HorizontalFieldValue
              label={tr(`AddEditLocationSidebar.streetNr`, `Street and number`)}
              value={
                <>
                  <InputText
                    style={{ width: '100%' }}
                    name={'streetNr'}
                    value={form.values.streetNr}
                    onChange={e => form.setFieldValue(names.streetNr, e.target.value)}
                  />
                  <Error>{form.errors.streetNr}</Error>
                </>
              }
            />
          </Row>
          <Row>
            <HorizontalFieldValue
              label={tr(`AddEditLocationSidebar.timezone`, `Timezone`)}
              value={
                <>
                  <InputTimezone
                    value={form.values.timezoneId}
                    onChange={v => form.setFieldValue(names.timezoneId, v)}
                  />
                </>
              }
            />
          </Row>
          <Row>
            <HorizontalFieldValue
              label={tr(`generic.locationNote`, `Location note`)}
              value={
                <RichTextEditor
                  value={form.values.locationNote}
                  onChange={v => form.setFieldValue(names.locationNote, v)}
                  maxLength={NOTE_SOFT_LIMIT}
                  allowCodeBlock={false}
                />
              }
            />
          </Row>
          <Row>
            <HorizontalFieldValue
              label={tr(`AddEditLocationSidebar.concurrentlyActiveEventsCountLabel`, `Concurrently active events count`)}
              value={
                <>
                  <InputText
                    style={{ width: '100%' }}
                    name={'concurrentlyActiveEventsCount'}
                    value={form.values.concurrentlyActiveEventsCount}
                    onChange={e => form.setFieldValue(names.concurrentlyActiveEventsCount, e.target.value)}
                  />
                  <Error>{form.errors.concurrentlyActiveEventsCount}</Error>
                </>
              }
            />
            <HorizontalFieldValue
              label={tr(`AddEditRamp.isNotIncludedPrivateRamps`, `Not Include private ramps`)}
              value={<InputYesNoPlain value={form.values.isNotIncludedPrivateRamps}
                                      onChange={v => form.setFieldValue(names.isNotIncludedPrivateRamps, v)} />}
            />
          </Row>
          <Row>
            <HorizontalFieldValue
              label={tr(`AddEditLocationSidebar.externalIds`, `External location IDs`)}
              value={
                <>
                  <Chips
                    className={'w-full'}
                    separator=","
                    value={form.values.externalIds}
                    onChange={e => form.setFieldValue(names.externalIds, e.value ?? [])}
                  />
                  <Error>{form.errors.externalIds}</Error>
                </>
              }
            />
          </Row>
          <div className="flex flex-col gap-2 p-2 mt-2 bg-base-quaternary">
            <div className="grid grid-cols-2 gap-4 w-full">
              <Field
                label={tr(`AddEditLocationSidebar.contactName`, `Contact name`)}
                required={false}
              >
                <InputText
                  value={form.values.contactName}
                  onChange={e => form.setFieldValue(form.names.contactName, e.target.value)}
                />
              </Field>

              <Field
                label={tr(`AddEditLocationSidebar.contactPhone`, `Contact phone`)}
              >
                <InputText
                  value={form.values.contactPhone}
                  onChange={e => form.setFieldValue(form.names.contactPhone, e.target.value)}
                />
              </Field>
            </div>
            <div className="w-full">
              <Field
                label={tr(`AddEditLocationSidebar.contactEmail`, `Contact email`)}
                required={false}
                errorMessage={findValidationMessage(form.validationErrors, form.names.contactEmail, tr)}
              >
                <InputText
                  value={form.values.contactEmail}
                  onChange={e => form.setFieldValue(form.names.contactEmail, e.target.value)}
                />
              </Field>
            </div>
            <div className="w-full items-center">
              <RingilInputSwitch
                label={tr(`AddEditLocationSidebar.automaticPrefillToNotify`, `Automatic notification prefill`)}
                value={form.values.contactAutomaticPrefillToNotify}
                onChange={value => form.setFieldValue(form.names.contactAutomaticPrefillToNotify, value)}
              />
            </div>

          </div>
          <Row className={'mt-2'}>
            <RingilInputSwitch label={tr(`AddEditLocationSidebar.enableVisitors`, `Enable visitors`)}
                               value={form.values.enableVisitor}
                               onChange={v => form.setFieldValue(names.enableVisitor, v)} />
          </Row>
          {cmrSigningEnabled && (
            <>
              <Row className={'mt-2'}>
                <RingilInputSwitch label={tr(`AddEditLocationSidebar.senderSigningEnabled`, `Sender signing enabled`)}
                                   value={form.values.senderSigningEnabled}
                                   onChange={v => form.setFieldValue(names.senderSigningEnabled, v)} />
              </Row>
              {form.values.senderSigningEnabled && (
                <div className={'ml-2'}>
                  <Row className={'mt-2'}>
                    <RingilInputSwitch
                      label={tr(`AddEditLocationSidebar.senderSigningTextEnabled`, `Sender text signing enabled`)}
                      value={form.values.senderSigningData?.textEnabled ?? false}
                      onChange={v => form.setFieldValue('senderSigningData.textEnabled', v)}
                    />
                  </Row>
                  {form.values.senderSigningData?.textEnabled && (
                    <Row className={'mt-2'}>
                      <InputText
                        style={{ width: '100%' }}
                        value={form.values.senderSigningData?.text ?? ''}
                        onChange={e => form.setFieldValue('senderSigningData.text', e.target.value)}
                        placeholder={'Text'}
                      />
                    </Row>
                  )}
                  <Row className={'mt-2'}>
                    <RingilInputSwitch
                      label={tr(`AddEditLocationSidebar.senderSigningImageEnabled`, `Sender image signing enabled`)}
                      value={form.values.senderSigningData?.imageEnabled ?? false}
                      onChange={v => form.setFieldValue('senderSigningData.imageEnabled', v)}
                    />
                  </Row>
                  {form.values.senderSigningData?.imageEnabled && (
                    <Row className={'mt-2'}>
                      {form.values.senderSigningData?.imageData ? (
                        <COItemDisplay
                          icon={<SvgDocument />}
                          label={form.values.senderSigningData.imageData.fileName}
                          rightIcon={
                            <SvgDeleteIcon
                              className={'cursor-pointer'}
                              onClick={() => {
                                deleteFile('senderSigningData.imageData');
                              }}
                            />
                          }
                        />
                      ) : (
                        <>
                          <UploadPanelVisual
                            onFiles={files => processFiles('senderSigningData', files)}
                            onClick={() => handleClick(inputFileSenderRef)}
                            showDropZone
                          />
                          <input
                            type="file"
                            ref={inputFileSenderRef}
                            onChange={e => handleFileSelection('senderSigningData', e)}
                            className="hidden"
                            multiple
                          />
                        </>
                      )}
                    </Row>
                  )}
                  <Row className={'mt-2'}>
                    <RingilInputSwitch
                      label={tr(`AddEditLocationSidebar.senderSigningUserEnabled`, `Sender user signing enabled`)}
                      value={form.values.senderSigningData?.userEnabled ?? false}
                      onChange={v => form.setFieldValue('senderSigningData.userEnabled', v)}
                    />
                  </Row>
                </div>
              )}
              <Row className={'mt-2'}>
                <RingilInputSwitch
                  label={tr(`AddEditLocationSidebar.receiverSigningEnabled`, `Receiver signing enabled`)}
                  value={form.values.receiverSigningEnabled}
                  onChange={v => form.setFieldValue(names.receiverSigningEnabled, v)} />
              </Row>
              {form.values.receiverSigningEnabled && (
                <div className={'ml-2'}>
                  <Row className={'mt-2'}>
                    <RingilInputSwitch
                      label={tr(`AddEditLocationSidebar.receiverSigningTextEnabled`, `Receiver text signing enabled`)}
                      value={form.values.receiverSigningData?.textEnabled ?? false}
                      onChange={v => form.setFieldValue('receiverSigningData.textEnabled', v)}
                    />
                  </Row>
                  {form.values.receiverSigningData?.textEnabled && (
                    <Row className={'mt-2'}>
                      <InputText
                        style={{ width: '100%' }}
                        value={form.values.receiverSigningData?.text ?? ''}
                        onChange={e => form.setFieldValue('receiverSigningData.text', e.target.value)}
                        placeholder={'Text'}
                      />
                    </Row>
                  )}
                  <Row className={'mt-2'}>
                    <RingilInputSwitch
                      label={tr(`AddEditLocationSidebar.receiverSigningImageEnabled`, `Receiver image signing enabled`)}
                      value={form.values.receiverSigningData?.imageEnabled ?? false}
                      onChange={v => form.setFieldValue('receiverSigningData.imageEnabled', v)}
                    />
                  </Row>
                  {form.values.receiverSigningData?.imageEnabled && (
                    <Row className={'mt-2'}>
                      {form.values.receiverSigningData?.imageData ? (
                        <COItemDisplay
                          icon={<SvgDocument />}
                          label={form.values.receiverSigningData.imageData.fileName}
                          rightIcon={
                            <SvgDeleteIcon
                              className={'cursor-pointer'}
                              onClick={() => {
                                deleteFile('receiverSigningData.imageData');
                              }}
                            />
                          }
                        />
                      ) : (
                        <>
                          <UploadPanelVisual
                            onFiles={files => processFiles('receiverSigningData', files)}
                            onClick={() => handleClick(inputFileReceiverRef)}
                            showDropZone
                          />
                          <input
                            type="file"
                            ref={inputFileReceiverRef}
                            onChange={e => handleFileSelection('receiverSigningData', e)}
                            className="hidden"
                            multiple
                          />
                        </>
                      )}
                    </Row>
                  )}
                  <Row className={'mt-2'}>
                    <RingilInputSwitch
                      label={tr(`AddEditLocationSidebar.receiverSigningUserEnabled`, `Receiver user signing enabled`)}
                      value={form.values.receiverSigningData?.userEnabled ?? false}
                      onChange={v => form.setFieldValue('receiverSigningData.userEnabled', v)}
                    />
                  </Row>
                </div>
              )}
            </>
          )}
          <Row className={'mt-2'}>
            <RingilInputSwitch label={tr(`AddEditLocationSidebar.delete`, `Mark as deleted`)}
                               value={form.values.isDeleted}
                               onChange={v => form.setFieldValue(names.isDeleted, v)} />
          </Row>
          <COHeadline
            title={tr(`AddEditLocationSidebar.additionalContacts`, `Additional contacts`)}
            variant={'h3'}
            className={'mt-2'}
          />
          {form.values.contacts.map((contact, contactIndex) => {
            return (
              <div key={contactIndex} className="flex flex-col gap-2 p-2 mt-2 bg-base-quaternary">
                <div className="grid grid-cols-2 gap-4 w-full">
                  <Field
                    label={tr(`AddEditLocationSidebar.contactName`, `Contact name`)}
                    required={!form.values.contacts[contactIndex]?.contactName}
                    errorMessage={findValidationMessage(form.validationErrors, `contacts[${contactIndex}].contactName`, tr)}
                  >
                    <InputText
                      value={form.values.contacts[contactIndex]?.contactName}
                      onChange={e => form.setFieldValue(`${names.contacts}[${contactIndex}].contactName`, e.target.value)}
                    />
                  </Field>

                  <Field
                    label={tr(`AddEditLocationSidebar.contactPhone`, `Contact phone`)}
                  >
                    <InputText
                      value={form.values.contacts[contactIndex]?.contactPhone}
                      onChange={e => form.setFieldValue(`${names.contacts}[${contactIndex}].contactPhone`, e.target.value)}
                    />
                  </Field>
                </div>
                <div className="w-full">
                  <Field
                    label={tr(`AddEditLocationSidebar.contactEmail`, `Contact email`)}
                    required={!form.values.contacts[contactIndex]?.contactEmail}
                    errorMessage={findValidationMessage(form.validationErrors, `contacts[${contactIndex}].contactEmail`, tr)}
                  >
                    <InputText
                      value={form.values.contacts[contactIndex]?.contactEmail}
                      onChange={e => form.setFieldValue(`${names.contacts}[${contactIndex}].contactEmail`, e.target.value)}
                    />
                  </Field>
                </div>
                <div className="flex flex-row w-full items-center justify-between">
                  <RingilInputSwitch
                    label={tr(`AddEditLocationSidebar.automaticPrefillToNotify`, `Automatic notification prefill`)}
                    value={form.values.contacts[contactIndex]?.automaticPrefillToNotify}
                    onChange={value => form.setFieldValue(`${names.contacts}[${contactIndex}].automaticPrefillToNotify`, value)}
                  />
                  <div className="flex justify-end">
                    <SvgTrashBin
                      className="cursor-pointer text-complementary-error"
                      onClick={() => removeContactPerson(contactIndex)}
                    />
                  </div>
                </div>

              </div>
            );
          })}
          <div className="mt-2">
            <Button label={tr(`AddEditLocationSidebar.addContact`, `Add contact`)} onClick={e => addContactPerson()} />
          </div>
          <Col>
            <InputACL
              acl={form.values.acl}
              moduleTypes={[ModulesEnum.SUPPLIER_NOTIFICATION, ModulesEnum.TIMESLOTS]}
              onChange={v => form.setFieldValue(names.acl, v)}
            />
            <ValidationDiv errors={form.validationErrors} path={'acl'} />
          </Col>
          <Col>
            <RingilInputSwitch label={tr(`AddEditLocationSidebar.partner_location`, `Partnerská lokace`)}
                               value={form.values.isPartnerLocation}
                               onChange={v => form.setFieldValue(names.isPartnerLocation, v)}
                               disabled={form.values.ramps.length > 0} />
          </Col>
          <div className="flex flex-col mt-2 gap-2">
            <COHeadline title={tr(`AddEditLocationSidebar.shareLocation`, `Share location`)} variant={'h2'} />
            {form.values.sharing?.map((sharingCompany, index) => (
              <div className="grid grid-cols-11 gap-2 items-start">
                <div className="col-span-5 flex flex-col gap-2">
                  <Dropdown
                    value={sharingCompany.companyId}
                    options={companies}
                    onChange={(e: any) => form.setFieldValue(`${names.sharing}[${index}].companyId`, e.value)}
                    optionLabel={'name'}
                    optionValue={'id'}
                  />
                  <ValidationDiv errors={form.validationErrors} path={`${names.sharing}[${index}].companyId`} />
                </div>
                <div className="col-span-5 flex flex-col gap-2">
                  <Dropdown
                    value={sharingCompany.locationSharingPrivilege}
                    options={sharingPrivilegies}
                    onChange={(e: any) => form.setFieldValue(`${names.sharing}[${index}].locationSharingPrivilege`, e.value)}
                    optionLabel={'name'}
                    optionValue={'code'}
                  />
                  <ValidationDiv errors={form.validationErrors}
                                 path={`${names.sharing}[${index}].locationSharingPrivilege`} />
                </div>
                <div className="col-span-1 flex items-start justify-center pt-1">
                  <RingilButton
                    mode={'table'}
                    icon={<SvgDelete />}
                    onClick={() =>
                      form.setFieldValue(
                        names.sharing,
                        form.values.sharing?.filter((_, i) => i !== index) ?? [],
                      )}
                  />

                </div>
              </div>
            ))}
            <RingilButton
              mode={'filled-rectangle'}
              label={tr(`AddEditLocationSidebar.addCompanyToShare`, `Add company to share with`)}
              onClick={() => {
                form.setFieldValue(names.sharing, [...form.values.sharing, {
                  companyId: null,
                  locationSharingPrivilege: null,
                }]);
              }}
            />
          </div>
          {/* The column bound comes from the backend config on the location, so this is an edit-only block. */}
          {locationId && (
            <div className={'flex flex-col gap-3 mt-2'}>
              <COHeadline title={tr(`AddEditLocationSidebar.tvDashboard`, `Internal TV dashboard`)} variant={'h2'} />
              <TvDashboardSettingsEditor
                value={form.values.tvDashboard}
                maxColumns={form.values.tvMaxColumns}
                onChange={v => form.setFieldValue(names.tvDashboard, v)}
                dataTestId={'location-tvDashboard'}
              />
            </div>
          )}
          {loggedUser?.features?.some(t => t == 'DACHSER_API') && (
            <>
              <Row>
                <HorizontalFieldValue
                  label={tr(`AddEditLocationSidebar.dachserBranch`, `Dachser branch`)}
                  value={
                    <>
                      <InputText
                        style={{ width: '100%' }}
                        name={'dachserBranch'}
                        value={form.values.dachserBranch}
                        onChange={e => form.setFieldValue(names.dachserBranch, e.target.value)}
                      />
                    </>
                  }
                />
                <HorizontalFieldValue
                  label={tr(`AddEditLocationSidebar.dachserCustomerNumber`, `Dachser customer number`)}
                  value={
                    <>
                      <InputText
                        style={{ width: '100%' }}
                        name={'dachserCustomerNumber'}
                        value={form.values.dachserCustomerNumber}
                        onChange={e => form.setFieldValue(names.dachserCustomerNumber, e.target.value)}
                      />
                    </>
                  }
                />
              </Row>
              <Row>
                <HorizontalFieldValue
                  label={tr(`AddEditLocationSidebar.dachserToken`, `Dachser token`)}
                  value={
                    <>
                      <InputText
                        style={{ width: '100%' }}
                        name={'dachserToken'}
                        value={form.values.dachserToken}
                        onChange={e => form.setFieldValue(names.dachserToken, e.target.value)}
                      />
                    </>
                  }
                />
              </Row>
            </>
          )}
          {hasFeature([FeaturesEnum.SCHENKER_API]) && (
            <>
              <Row>
                <HorizontalFieldValue
                  label={tr(`AddEditLocationSidebar.schenkerReferenceNumber`, `Schenker reference number`)}
                  value={
                    <>
                      <InputText
                        style={{ width: '100%' }}
                        name={'schenkerReferenceNumber'}
                        value={form.values.schenkerReferenceNumber}
                        onChange={e => form.setFieldValue(names.schenkerReferenceNumber, e.target.value)}
                      />
                    </>
                  }
                />
              </Row>
            </>
          )}
          {hasFeature([FeaturesEnum.DHL_API]) && (
            <>
              <Row>
                <HorizontalFieldValue
                  label={tr(`AddEditLocationSidebar.dhlAccountNumberImport`, `DHL account number import`)}
                  value={
                    <>
                      <InputText
                        style={{ width: '100%' }}
                        name={'dhlAccountNumberImport'}
                        value={form.values.dhlAccountNumberImport}
                        onChange={e => form.setFieldValue(names.dhlAccountNumberImport, e.target.value)}
                      />
                    </>
                  }
                />
              </Row>
              <Row>
                <HorizontalFieldValue
                  label={tr(`AddEditLocationSidebar.dhlAccountNumberExport`, `DHL account number export`)}
                  value={
                    <>
                      <InputText
                        style={{ width: '100%' }}
                        name={'dhlAccountNumberExport'}
                        value={form.values.dhlAccountNumberExport}
                        onChange={e => form.setFieldValue(names.dhlAccountNumberExport, e.target.value)}
                      />
                    </>
                  }
                />
              </Row>
            </>
          )}
          {fedexEnabled && (
            <Row>
              <HorizontalFieldValue
                label={tr('LocationCarriersApi.fedexAccounts', 'FedEx accounts allowed for this location')}
                value={
                  <MultiSelect
                    style={{ width: '100%' }}
                    value={form.values.fedexAccountIds ?? []}
                    options={fedexAccounts}
                    optionValue={'id'}
                    optionLabel={'accountNumber'}
                    itemTemplate={(account: FedexAccountOutDTO) => fedexAccountLabel(account)}
                    onChange={e => form.setFieldValue(names.fedexAccountIds, e.value ?? [])}
                    placeholder={tr('LocationCarriersApi.fedexAccountsAll', 'All company accounts')}
                    display={'chip'}
                  />
                }
              />
            </Row>
          )}
      </ColumnRow>
    </RingilSidebar>
  );
};

const ColumnRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const Error = styled.div`
  display: flex;
  color: red;
`;

export default AddEditLocationSidebar;
